'use client';

// ═══════════════════════════════════════════════════════════════════════
// usePendingReviewQueue — global polling hook + dismissed-set state.
//
// SWR-polls /api/portfolio/pending-reviews every 5 seconds while the user
// is authenticated. Surfaces:
//   • queue:        the full active queue (server-authoritative).
//   • current:      the item the popup should show NOW. The user can
//                   "Dismiss until next" via `dismissCurrent` — that hides
//                   the item locally without resolving the server row,
//                   and a NEWER item arriving (or the current one being
//                   resolved from another tab) re-opens the slide.
//   • approve / reject / cancelSnooze: typed wrappers that mutate the
//                   queue, then re-fetch.
//
// "Dismiss until next" works by tracking the dismissed review id in
// component state. When a strictly-newer review id appears in the queue,
// we drop the dismissed marker and surface the new one.
// ═══════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import {
  getPendingReviews,
  approvePendingReview,
  rejectPendingReview,
  cancelPendingReviewSnooze,
  type PendingReview,
  type PendingReviewApproveResult,
  type PendingReviewRejectResolution,
  type PendingReviewSkipResult,
} from '@/lib/api-client';
import { useAuth } from '@/context/AuthContext';

const POLL_MS = 5_000;
const KEY = '/portfolio/pending-reviews';

export interface UsePendingReviewQueueResult {
  /** All active reviews (server-side filtered to status=pending && not expired). */
  queue: PendingReview[];
  /** The item to surface right now — first un-dismissed entry, or null. */
  current: PendingReview | null;
  /** Count of items beyond `current` in the queue. UI shows "+N more". */
  moreCount: number;
  /** True while SWR is between the initial fetch and the first response. */
  loading: boolean;
  /** Most recent fetch error (transient — SWR will retry on next interval). */
  error: Error | null;
  /** Mark the current review as "dismissed until next." Does NOT call the
   *  server — the review row stays pending. */
  dismissCurrent: () => void;
  /** Clear the dismissed-set entirely (e.g. on logout). */
  resetDismissed: () => void;
  /** Partial approve — fires the listed tickers and returns the
   *  executor's per-ticker outcome. The acted tickers drop from the
   *  review's candidates server-side; the queue re-fetch reflects
   *  that on the next SWR cycle. */
  approve: (id: string, input: { tickers: string[]; reason?: string }) => Promise<PendingReviewApproveResult>;
  /** Reject — skip (with or without tickers for partial / whole-row),
   *  snooze (portfolio-wide), manual_hold (sell only). */
  reject: (id: string, input: { resolution: PendingReviewRejectResolution; tickers?: string[]; snoozeMinutes?: number; reason?: string }) => Promise<PendingReview | PendingReviewSkipResult>;
  /** Clear an active per-portfolio snooze. */
  cancelSnoozeForReview: (id: string) => Promise<{ portfolioId: string; cleared: boolean }>;
  /** Force-refresh the queue (e.g. after a click that should re-pull). */
  refresh: () => Promise<unknown>;
}

export function usePendingReviewQueue(): UsePendingReviewQueueResult {
  const { user, loading: authLoading } = useAuth();
  const isAuthenticated = !!user && !authLoading;
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const { data, error, isLoading, mutate } = useSWR<PendingReview[]>(
    isAuthenticated ? KEY : null,
    () => getPendingReviews(),
    {
      refreshInterval: POLL_MS,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      // Keep showing the last good queue while a refetch is in flight —
      // avoids the popup flickering closed every 5s on slow networks.
      keepPreviousData: true,
    },
  );

  const queue = useMemo<PendingReview[]>(() => (Array.isArray(data) ? data : []), [data]);

  const current = useMemo<PendingReview | null>(() => {
    if (queue.length === 0) return null;
    // Auto-drop dismissed ids that are no longer in the queue (the row
    // got resolved server-side, or expired). Without this the dismissed
    // set grows unbounded across session.
    //
    // dismissed is in the deps so clicking Dismiss recomputes `current`
    // immediately (previously this read dismissedRef and only depended
    // on `queue`, which meant the popup didn't hide until the next SWR
    // poll updated the queue — surfaced as "Dismiss does nothing" when
    // the tab had been idle long enough that polling paused).
    const live = new Set(queue.map((r) => r.id));
    let pruned = false;
    const next = new Set<string>();
    for (const id of dismissed) {
      if (live.has(id)) next.add(id);
      else pruned = true;
    }
    if (pruned && next.size !== dismissed.size) {
      // Schedule state update outside the render — cheap, async, idempotent.
      setTimeout(() => setDismissed(next), 0);
    }
    const firstShowable = queue.find((r) => !next.has(r.id));
    return firstShowable ?? null;
  }, [queue, dismissed]);

  const moreCount = useMemo(() => {
    if (!current) return 0;
    return Math.max(0, queue.length - 1 - queue.findIndex((r) => r.id === current.id));
  }, [queue, current]);

  // Cross-tab dismiss sync. Without this, dismissing in one tab
  // leaves the side-slide live in any other tab the user has open.
  // BroadcastChannel posts the dismissed id; siblings add it to their
  // own dismissed Set on receipt. resetDismissed (panel-close) is
  // also broadcast so closing the panel in tab A clears tab B's
  // suppressions in lockstep. Same channel used to nudge a refetch
  // after a server-side reject (snooze / approve) — siblings would
  // otherwise see the stale row until their next 5s SWR poll.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel('pending-review-queue');
    const onMessage = (e: MessageEvent) => {
      const m = e.data as { type?: string; id?: string };
      if (!m || typeof m.type !== 'string') return;
      if (m.type === 'dismiss' && typeof m.id === 'string') {
        const id = m.id;
        setDismissed((prev) => prev.has(id) ? prev : new Set(prev).add(id));
      } else if (m.type === 'reset') {
        setDismissed(new Set());
      } else if (m.type === 'invalidate') {
        // A sibling tab took a server action; refetch so this tab's
        // queue reflects the change immediately rather than after the
        // next poll. mutate() is idempotent.
        void mutate();
      }
    };
    ch.addEventListener('message', onMessage);
    return () => { ch.removeEventListener('message', onMessage); ch.close(); };
  }, [mutate]);

  const broadcast = useCallback((msg: { type: string; id?: string }) => {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;
    try {
      const ch = new BroadcastChannel('pending-review-queue');
      ch.postMessage(msg);
      ch.close();
    } catch { /* ignore — BroadcastChannel can be unavailable in some contexts */ }
  }, []);

  const dismissCurrent = useCallback(() => {
    if (!current) return;
    const id = current.id;
    setDismissed((prev) => prev.has(id) ? prev : new Set(prev).add(id));
    broadcast({ type: 'dismiss', id });
  }, [current, broadcast]);

  const resetDismissed = useCallback(() => {
    setDismissed(new Set());
    broadcast({ type: 'reset' });
  }, [broadcast]);

  const approve = useCallback(async (id: string, input: { tickers: string[]; reason?: string }) => {
    const result = await approvePendingReview(id, input);
    await mutate();
    // When the partial action terminates the row, drop the dismiss
    // marker so the next pending review can re-pop the side-slide.
    if (result.status !== 'pending') {
      setDismissed((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
    broadcast({ type: 'invalidate' });
    return result;
  }, [mutate, broadcast]);

  const reject = useCallback(async (id: string, input: { resolution: PendingReviewRejectResolution; tickers?: string[]; snoozeMinutes?: number; reason?: string }) => {
    const result = await rejectPendingReview(id, input);
    await mutate();
    // Whole-row reject terminates the review; clear the dismiss mark.
    // Partial skip leaves remainder pending — keep dismiss intact so
    // the user can keep navigating the queue without a slide re-pop.
    const isTerminal = 'status' in result
      ? result.status !== 'pending'
      : !('remaining' in result) || (result as PendingReviewSkipResult).remaining === 0;
    if (isTerminal) {
      setDismissed((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
    broadcast({ type: 'invalidate' });
    return result;
  }, [mutate, broadcast]);

  const cancelSnoozeForReview = useCallback(async (id: string) => {
    const result = await cancelPendingReviewSnooze(id);
    await mutate();
    broadcast({ type: 'invalidate' });
    return result;
  }, [mutate, broadcast]);

  const refresh = useCallback(() => mutate(), [mutate]);

  return {
    queue,
    current,
    moreCount,
    loading: isLoading,
    error: (error as Error) ?? null,
    dismissCurrent,
    resetDismissed,
    approve,
    reject,
    cancelSnoozeForReview,
    refresh,
  };
}
